import { useQuery } from "@tanstack/react-query";
import type { CircleMemberGoalsResponse, SharedCircleGoalResponse } from "@consistency-circle/shared";
import { UsersIcon } from "lucide-react";

import { Progress } from "@/components/ui/progress";

import { apiClient } from "../../../lib/api";
import { useAuthSession } from "../../../lib/auth-client";

type SharedGoalTodayItem = {
  readonly circleId: string;
  readonly circleName: string;
  readonly ownerId: string;
  readonly ownerName: string;
  readonly goal: SharedCircleGoalResponse;
};

type CircleProgressSection = {
  readonly circleId: string;
  readonly circleName: string;
  readonly members: CircleMemberGoalsResponse[];
};

function flattenSharedGoals(sections: readonly CircleProgressSection[]): SharedGoalTodayItem[] {
  return sections.flatMap((section) =>
    section.members.flatMap((member) =>
      member.goals.map((goal) => ({
        circleId: section.circleId,
        circleName: section.circleName,
        ownerId: goal.ownerUserId,
        ownerName: goal.ownerDisplayName,
        goal,
      })),
    ),
  );
}

function summarizeGoal(goal: SharedCircleGoalResponse) {
  const total = goal.slots.length;
  const done = goal.slots.filter((slot) => slot.status === "DONE").length;
  const skipped = goal.slots.filter((slot) => slot.status === "SKIPPED").length;
  const missed = goal.slots.filter((slot) => slot.status === "MISSED").length;
  const open = goal.slots.filter((slot) => slot.status === "DUE_LATER").length;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);

  return { total, done, skipped, missed, open, percent };
}

export function SharedGoalsToday() {
  const { data: session } = useAuthSession();

  const { data: circles } = useQuery({
    queryKey: ["circles"],
    queryFn: () => apiClient.getCircles(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: sections } = useQuery({
    queryKey: ["today-shared-goals", session?.user.id, circles?.map((circle) => circle.id) ?? []],
    enabled: !!session && !!circles && circles.length > 0,
    queryFn: async (): Promise<CircleProgressSection[]> => {
      if (!circles || circles.length === 0) {
        return [];
      }

      const progress = await Promise.all(
        circles.map(async (circle) => ({
          circle,
          progress: await apiClient.getCircleProgress(circle.id),
        })),
      );

      return progress
        .map(({ circle, progress }) => ({
          circleId: circle.id,
          circleName: circle.name,
          members: progress.members.filter(
            (member) => member.userId !== session?.user.id && member.goals.length > 0,
          ),
        }))
        .filter((section) => section.members.length > 0);
    },
  });

  const goals = flattenSharedGoals(sections ?? []);

  if (goals.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3 rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            Shared goals
          </p>
          <h2 className="text-sm font-semibold">Today in your circles</h2>
        </div>
        <UsersIcon className="h-4 w-4 text-muted-foreground" />
      </div>

      <div className="space-y-3">
        {goals.slice(0, 4).map((item) => {
          const summary = summarizeGoal(item.goal);

          return (
            <div key={`${item.circleId}:${item.ownerId}:${item.goal.id}`} className="space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{item.goal.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.ownerName}'s goal - {item.circleName}
                  </p>
                </div>
                <span className="shrink-0 text-xs font-semibold tabular-nums">
                  {summary.total === 0 ? "-" : `${summary.done}/${summary.total}`}
                </span>
              </div>
              <Progress value={summary.percent} className="h-1.5" />
              <p className="text-[11px] text-muted-foreground">
                {summary.done} done
                {summary.open > 0 ? ` - ${summary.open} open` : ""}
                {summary.skipped > 0 ? ` - ${summary.skipped} skipped` : ""}
                {summary.missed > 0 ? ` - ${summary.missed} missed` : ""}
              </p>
            </div>
          );
        })}
      </div>

      {goals.length > 4 && (
        <p className="text-xs text-muted-foreground">
          {goals.length - 4} more shared goal{goals.length - 4 === 1 ? "" : "s"} in Review.
        </p>
      )}
    </section>
  );
}
